/**
 * api.js — Capa de servicios alineada 100% con api.php
 *
 * Endpoints del backend PHP:
 * POST   api/auth/login
 * GET    api/auth/me
 * POST   api/auth/change-password          (solo superadmin)
 *
 * GET    api/admin/clients
 * POST   api/admin/clients
 * PATCH  api/admin/clients/:id
 * DELETE api/admin/clients/:id
 * POST   api/admin/clients/:id/change-password
 *
 * GET    api/admin/template
 * PUT    api/admin/template/config
 * POST   api/admin/template/productos
 * PUT    api/admin/template/productos/:id
 * DELETE api/admin/template/productos/:id
 *
 * GET    api/client/store
 * PUT    api/client/store/config
 * POST   api/client/store/productos
 * PUT    api/client/store/productos/:id
 * DELETE api/client/store/productos/:id
 *
 * GET    api/store/:clientId               (público, sin auth)
 * POST   api/mp/create-preference          (público)
 * POST   api/mp/create-preference/:clientId(público alternativo)
 * POST   api/mp/webhook                    (público)
 */

// En producción Hostinger: mismo dominio, rutas relativas
// En dev local: proxy a php -S localhost:8000
const BASE = process.env.NODE_ENV === 'production' ? '/api' : '/api';

function getToken() {
  return localStorage.getItem('saas_token');
}

async function req(method, path, body = null, auth = true) {
  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const t = getToken();
    if (t) headers['Authorization'] = `Bearer ${t}`;
  }

  const opts = { method, headers };
  if (body && method !== 'GET') opts.body = JSON.stringify(body);

  const res = await fetch(`${BASE}/${path}`, opts);

  // Intentar parsear JSON siempre
  let data;
  try {
    data = await res.json();
  } catch {
    data = {};
  }

  if (!res.ok) {
    // El PHP devuelve { error: "mensaje" }
    throw new Error(data.error || data.message || `Error ${res.status}`);
  }

  return data;
}

// ─── Auth ──────────────────────────────────────────────────────────────────────
// POST api/auth/login → { token, role, username, clientId? }
export const login = (username, password) =>
  req('POST', 'auth/login', { username, password }, false);

// GET api/auth/me → { username, role, clientId?, active?, expiresAt? }
export const getMe = () => req('GET', 'auth/me');

// POST api/auth/change-password (solo superadmin)
export const changePassword = (currentPassword, newPassword, confirmPassword) =>
  req('POST', 'auth/change-password', { currentPassword, newPassword, confirmPassword });

// ─── Admin: Clientes ──────────────────────────────────────────────────────────
// GET api/admin/clients → array de clientes (sin password)
export const getClients = () => req('GET', 'admin/clients');

// POST api/admin/clients → cliente creado
export const createClient = ({ username, password, storeName, expiresAt }) =>
  req('POST', 'admin/clients', { username, password, storeName, expiresAt });

// PATCH api/admin/clients/:id → cliente actualizado
export const updateClient = (id, patch) => req('PATCH', `admin/clients/${id}`, patch);

// DELETE api/admin/clients/:id → { success: true }
export const deleteClient = (id) => req('DELETE', `admin/clients/${id}`);

// POST api/admin/clients/:id/change-password
export const changeClientPassword = (id, newPassword, confirmPassword) =>
  req('POST', `admin/clients/${id}/change-password`, { newPassword, confirmPassword });

// ─── Admin: Plantilla base ────────────────────────────────────────────────────
// GET api/admin/template → { config, productos }
export const getTemplate = () => req('GET', 'admin/template');

// PUT api/admin/template/config → config guardada
export const saveTemplateConfig = (config) => req('PUT', 'admin/template/config', config);

// POST api/admin/template/productos → producto creado
export const addTemplateProduct = (producto) => req('POST', 'admin/template/productos', producto);

// PUT api/admin/template/productos/:id → producto actualizado
export const updateTemplateProduct = (id, producto) =>
  req('PUT', `admin/template/productos/${id}`, producto);

// DELETE api/admin/template/productos/:id → { success: true }
export const deleteTemplateProduct = (id) => req('DELETE', `admin/template/productos/${id}`);

// ─── Cliente: Mi tienda ───────────────────────────────────────────────────────
// GET api/client/store → { config, productos }
export const getMyStore = () => req('GET', 'client/store');

// PUT api/client/store/config → config guardada
export const saveStoreConfig = (config) => req('PUT', 'client/store/config', config);

// POST api/client/store/productos → producto creado
export const addProduct = (producto) => req('POST', 'client/store/productos', producto);

// PUT api/client/store/productos/:id → producto actualizado
export const updateProduct = (id, producto) =>
  req('PUT', `client/store/productos/${id}`, producto);

// DELETE api/client/store/productos/:id → { success: true }
export const deleteProduct = (id) => req('DELETE', `client/store/productos/${id}`);

// ─── Upload de imágenes ───────────────────────────────────────────────────────
/**
 * Sube un archivo de imagen al servidor.
 * @param {File} file - Objeto File del input[type=file]
 * @returns {Promise<string>} URL pública de la imagen subida
 */
export async function uploadImage(file) {
  const token = getToken();
  const formData = new FormData();
  formData.append('imagen', file);

  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}/upload`, { method: 'POST', headers, body: formData });

  let data;
  try { data = await res.json(); } catch { data = {}; }
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data.url;
}

// ─── Público ──────────────────────────────────────────────────────────────────
// GET api/store/:clientId → { config, productos } (sin auth)
export const getPublicStore = (clientId) =>
  req('GET', `store/${clientId}`, null, false);

// POST api/mp/create-preference → { preference_id, init_point, sandbox_point }
export const createMpPreference = (clientId, producto, payer_email = '') =>
  req('POST', 'mp/create-preference', { clientId, producto, payer_email }, false);

// POST api/mp/payment → { id, status:'pending', ticket_url }
// Para pagos en efectivo (rapipago / pagofacil)
export const createCashPayment = (clientId, { payment_method_id, amount, description, payer_email }) =>
  req('POST', 'mp/payment', { clientId, payment_method_id, amount, description, payer_email }, false);
